import { createRoot } from 'react-dom/client';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
// root app page
import App from 'app';
// providers
import { ProjectProvider } from 'hooks/useProjectDetails';
import { ToastContextProvider } from 'hooks/useToast';
import { ProjectStepperContextProvider } from './hooks/useProjectStepperState';
// store
import { store } from 'redux-store/store';
// styles
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { InAppNotifications } from 'app/pk-in-app';
import 'swiper/css';
import { getEnvironmentConfig } from 'utils/environment';
import './index.css';

declare global {
  interface Window {
    jQuery?: any;
  }
}

// Load jQuery before GTM if not already loaded (required by GTM tags)
const loadJQueryIfNeeded = (): Promise<void> => {
  return new Promise((resolve) => {
    if (window.jQuery) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://code.jquery.com/jquery-3.7.1.min.js';
    script.integrity = 'sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo=';
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => {
      console.warn('Failed to load jQuery, GTM may not work correctly');
      resolve(); // Continue anyway
    };
    document.head.appendChild(script);
  });
};

const { isProduction, isRelease } = getEnvironmentConfig();

const tagManagerArgs = {
  gtmId: 'GTM-T8FPR29',
};
console.log('App Env: ', process.env.REACT_APP_ENV);
if (isProduction) {
  loadJQueryIfNeeded().then(() => {
    TagManager.initialize(tagManagerArgs);
  });
}

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

// Datadog configuration
if (isProduction || isRelease) {
  const datadogApplicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
  const datadogClientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
  const datadogEnv = process.env.REACT_APP_DATADOG_ENV;
  // Normalize environment name to lowercase (Datadog tags must be lowercase)
  const normalizedEnv = datadogEnv ? datadogEnv.toLowerCase() : 'dev';

  // Validate Datadog environment variables
  const isDatadogConfigured = datadogApplicationId && datadogClientToken;

  if (!isDatadogConfigured) {
    console.warn('⚠️ Datadog is not configured. Missing required environment variables:', {
      REACT_APP_DATADOG_APPLICATION_ID: !datadogApplicationId,
      REACT_APP_DATADOG_CLIENT_TOKEN: !datadogClientToken,
    });
  } else {
    try {
      // Initialize Datadog Browser Logs first (must be initialized before RUM)
      datadogLogs.init({
        clientToken: datadogClientToken,
        site: 'datadoghq.com',
        service: 'frontend',
        env: normalizedEnv,
        forwardErrorsToLogs: true,
      });

      // Initialize Datadog RUM
      datadogRum.init({
        applicationId: datadogApplicationId,
        clientToken: datadogClientToken,
        site: 'datadoghq.com',
        service: 'frontend',
        env: normalizedEnv,

        // --- BIGGEST COST SAVERS ---
        sessionSampleRate: 10, // from 100% → 10%
        sessionReplaySampleRate: 0, // disable replay (huge cost) or set to 5

        // --- OPTIONAL REDUCTIONS ---
        trackResources: false, // stops auto-capturing network/resource performance
        trackLongTasks: false, // stops high-frequency long task metrics
        defaultPrivacyLevel: 'mask', // better for PII and reduces replay data size

        plugins: [reactPlugin({ router: true })],
      });

      // Set up global error handler to send errors to Datadog
      window.addEventListener('error', (event) => {
        datadogLogs.logger.error('Unhandled error', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
        });
      });

      // Set up unhandled promise rejection handler
      window.addEventListener('unhandledrejection', (event) => {
        datadogLogs.logger.error('Unhandled promise rejection', {
          reason: event.reason,
          promise: event.promise,
        });
      });

      console.log('✅ Datadog initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Datadog:', error);
    }
  }
}

root.render(
  <BrowserRouter>
    <Provider store={store}>
      <ProjectProvider>
        <ProjectStepperContextProvider>
          <ToastContextProvider>
            <App />
            <InAppNotifications />
          </ToastContextProvider>
        </ProjectStepperContextProvider>
      </ProjectProvider>
    </Provider>
  </BrowserRouter>
);
